import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import { styled } from '../../../../stitches.config'
import { TextInputWithLabel } from '../../elements/Forms/TextInputWithLabel'
import CardWithLogo from '../cards/CardWithLogo'
import useGetPredesignPreviews from '../../../hooks/useGetPredesignPreviews'
import Image from 'next/image'
import orderBy from 'lodash/orderBy'
import trimStart from 'lodash/trimStart'

export interface PredesignPreviewProps {
    header: string
    visaOrMc: string
}

const PredesignContainer = styled('div', {
    display: 'flex',
    justifyContent: 'center',
})

const InputContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '@md': {
        width: '425px',
        marginRight: '100px',
    },
})

const BlueSubheading = styled('h4', {
    display: 'flex',
    alignItems: 'center',
    color: '#2C73FF',
    marginBottom: '5px',
    justifyContent: 'center',
    marginRight: '30px',
    '@md': {
        justifyContent: 'flex-start',
        marginRight: 0,
        marginBottom: '20px',
    },
})

const TryForYourSelfText = styled('span', {
    marginLeft: '10px',
    '@sm': {
        letterSpacing: '5px',
    },
})

const Predesign = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 20px',
    width: '90%',
    maxWidth: '400px',
    '@md': {
        flexDirection: 'row',
        maxWidth: '100%',
    },
})

const Heading = styled('h2', {
    margin: 0,
    textAlign: 'center',
    fontSize: '1.5rem',
    padding: '0 20px',
    '@sm': {
        fontSize: '2rem',
        padding: 0,
        textAlign: 'left',
    },
})

const Select = styled('select', {
    marginTop: '20px',
    width: '100%',
    maxWidth: '12rem',
    fontSize: '1rem',
    color: 'var(--colors-darkest)',
    border: '2px solid var(--colors-darkest)',
    borderRadius: 'var(--radii-2)',
    padding: 'var(--space-2)',
})

const PredesignImageContainer = styled('div', {
    marginTop: '20px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
})

const InputDivider = styled('div', {
    marginTop: '1rem',
    marginBottom: '1.5rem',
})

const cloudinaryBaseUrl =
    'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_200/v1669037165'

const PredesignPreview = ({ header, visaOrMc }: PredesignPreviewProps) => {
    const [nameOnCard, setNameOnCard] = useState<string>('')
    const [messageOnCard, setMessageOnCard] = useState<string>('')
    const [sampleCardImageId, setSampleCardImageId] = useState<string>('')
    const [occasions, setOccasions] = useState<any>([])
    const [selectedOccasionId, setSelectedOccasionId] = useState<string>('')
    const [selectedOccasion, setSelectedOccasion] = useState<any>('')

    function encodeEmbossLine(embossLine: string) {
        return encodeURIComponent(embossLine.trim().toUpperCase())
    }

    const defaultLogoImageUrl =
        'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_150/v1669036690/PGSite/marketing_images/sample-logo_w6dess.png'

    function getCloudinaryUrl(
        cardImageId: string,
        nameOnCard: string,
        messageOnCard: string
    ): string {
        const cleanedNameOnCard = encodeEmbossLine(nameOnCard)
        const cleanedMessageOnCard = encodeEmbossLine(messageOnCard)
        return `https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,fl_relative,h_1.0,l_GCG:card_overlays:${
            visaOrMc === 'mc'
                ? 'Mastercard_Reward_Number'
                : 'Visa_Reward_Number_vlbxnu'
        },w_1.0/co_white,fl_relative,g_south_west,l_text:Arial_28_stroke:${cleanedNameOnCard}%250A${cleanedMessageOnCard},x_0.1,y_0.1//c_scale,f_auto,h_208,w_325/${cardImageId}`
    }

    const handleSelectImage = (image: string) => {
        setSampleCardImageId(image)
    }

    const handleSelectChange = (e: any) => {
        setSelectedOccasionId(e.target.value)
    }

    const handleRemoveSpecialCharacters = (
        e: BaseSyntheticEvent,
        type: string
    ) => {
        const embossRegex = /[^-a-zA-Z0-9-\s]/

        const newValue = trimStart(
            e.currentTarget.value.toUpperCase().replace(embossRegex, '')
        )

        if (type === 'name') {
            setNameOnCard(newValue)
        }

        if (type === 'message') {
            setMessageOnCard(newValue)
        }
    }

    const { data: builderPredesignData, isError } = useGetPredesignPreviews()

    useEffect(() => {
        if (builderPredesignData && builderPredesignData.length > 0) {
            const orderedByPriority = orderBy(
                builderPredesignData,
                (occasion: any) => occasion.data.priority,
                ['asc']
            )

            setOccasions(orderedByPriority)
            setSelectedOccasion(orderedByPriority[0])
            setSampleCardImageId(orderedByPriority[0]?.data?.image1)
        }
    }, [builderPredesignData])

    useEffect(() => {
        const currentOccasion = occasions.filter(
            (occasion: any) => occasion.id === selectedOccasionId
        )
        setSelectedOccasion(currentOccasion[0])
        if (currentOccasion && currentOccasion.length > 0) {
            setSampleCardImageId(currentOccasion[0].data.image1)
        }
    }, [selectedOccasionId])

    const PredesignImage = ({ imageId, selectedOccasion }: any) => {
        return (
            <div style={{ position: 'sticky' }}>
                <Image
                    alt={'Reward card overlay'}
                    width={200}
                    height={75}
                    onClick={() => handleSelectImage(imageId)}
                    style={{
                        position: 'absolute',
                        width: '7.5rem',
                        borderRadius: '5px',
                    }}
                    src={
                        visaOrMc === 'mc'
                            ? `https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_100/v1636740618/GCG/card_overlays/mc-rewards-all-fields.png`
                            : 'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_100/v1669214381/GCG/smb_assets/reward-visa_tjokej.png'
                    }
                />
                <Image
                    alt={selectedOccasion.name}
                    width={200}
                    height={75}
                    onClick={() => handleSelectImage(imageId)}
                    style={{
                        width: '7.5rem',
                        borderRadius: '5px',
                    }}
                    src={`${cloudinaryBaseUrl}/${imageId}`}
                />
            </div>
        )
    }

    if (isError) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                An error occurred fetching previews.
            </div>
        )
    }

    return (
        <div style={{ minHeight: '45vh' }}>
            {occasions && occasions.length > 0 && selectedOccasion && (
                <PredesignContainer>
                    <Predesign>
                        <InputContainer>
                            <BlueSubheading>
                                <svg
                                    width="23"
                                    height="3"
                                    viewBox="0 0 23 3"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <line
                                        x1="0.5"
                                        y1="1.5"
                                        x2="22.5"
                                        y2="1.5"
                                        stroke="#2C73FF"
                                        strokeWidth="2"
                                    />
                                </svg>
                                <TryForYourSelfText>
                                    TRY FOR YOURSELF
                                </TryForYourSelfText>
                            </BlueSubheading>
                            <Heading>{header}</Heading>
                            <p>CUSTOMIZE TEXT:</p>
                            <div style={{ maxWidth: '400px' }}>
                                <TextInputWithLabel
                                    id={'nameOnCard'}
                                    type={'text'}
                                    value={nameOnCard}
                                    maxLength={20}
                                    placeholder={'Name printed on card *'}
                                    label={
                                        nameOnCard && nameOnCard.length > 0
                                            ? 'Name on Card'
                                            : ''
                                    }
                                    onChange={(e: BaseSyntheticEvent) => {
                                        handleRemoveSpecialCharacters(e, 'name')
                                    }}
                                />

                                <InputDivider>
                                    <TextInputWithLabel
                                        id={'messageOnCard'}
                                        type={'text'}
                                        value={messageOnCard}
                                        maxLength={20}
                                        placeholder={'Message printed on card'}
                                        label={''}
                                        onChange={(e: BaseSyntheticEvent) => {
                                            handleRemoveSpecialCharacters(
                                                e,
                                                'message'
                                            )
                                        }}
                                    />
                                </InputDivider>
                            </div>
                        </InputContainer>
                        <div>
                            <CardWithLogo
                                size={'lg'}
                                cardImageUrl={getCloudinaryUrl(
                                    sampleCardImageId,
                                    '',
                                    ''
                                )}
                                logoImageUrl={defaultLogoImageUrl}
                                nameOnCard={nameOnCard.toUpperCase()}
                                messageOnCard={messageOnCard.toUpperCase()}
                                overlayType={'full'}
                            />

                            <Select
                                value={selectedOccasionId}
                                onChange={handleSelectChange}
                            >
                                {occasions.map((occasion: any) => (
                                    <option
                                        value={occasion.id}
                                        key={occasion.id}
                                    >
                                        {occasion.name}
                                    </option>
                                ))}
                            </Select>

                            <PredesignImageContainer>
                                <PredesignImage
                                    selectedOccasion={selectedOccasion}
                                    imageId={selectedOccasion.data.image1}
                                />
                                <PredesignImage
                                    selectedOccasion={selectedOccasion}
                                    imageId={selectedOccasion.data.image2}
                                />
                                <PredesignImage
                                    selectedOccasion={selectedOccasion}
                                    imageId={selectedOccasion.data.image3}
                                />
                            </PredesignImageContainer>
                        </div>
                    </Predesign>
                </PredesignContainer>
            )}
        </div>
    )
}

export default PredesignPreview
